import { type ComponentProps } from 'react';
import { Check, Copy } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import { useCopy } from '@/hooks/useCopy';

type CopyButtonProps = {
  /** The text to copy to clipboard */
  value: string;
  /** Optional label shown next to the icon (e.g. "Copy" / "Copied") */
  label?: string;
  /** Tooltip text when not copied. Defaults to "Copy" */
  tooltip?: string;
  /** Aria-label for accessibility */
  ariaLabel?: string;
  /** Callback after successful copy (e.g. to show a toast) */
  onCopy?: () => void;
  /** Icon size class. Defaults to "h-4 w-4" */
  iconClassName?: string;
  message?: string;
} & Omit<ComponentProps<typeof Button>, 'onClick'>;

export function CopyButton({
  value,
  label,
  tooltip = 'Copy',
  ariaLabel,
  onCopy,
  iconClassName = 'h-4 w-4',
  message = 'Copied to clipboard',
  ...buttonProps
}: CopyButtonProps) {
  const { copiedContent, copyToClipboard } = useCopy({ message });

  const button = (
    <Button
      variant="ghost"
      size="icon"
      {...buttonProps}
      onClick={e => {
        e.stopPropagation();
        copyToClipboard(value);
      }}
      aria-label={ariaLabel ?? (copiedContent ? 'Copied' : tooltip)}
    >
      {copiedContent ? (
        <>
          <Check className={iconClassName} />
          {label && <span>{copiedContent ? 'Copied' : label}</span>}
        </>
      ) : (
        <>
          <Copy className={iconClassName} />
          {label && <span>{label}</span>}
        </>
      )}
    </Button>
  );

  // Only wrap with tooltip when there's no visible label
  if (label) return button;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent sideOffset={4}>{copiedContent ? 'Copied!' : tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
